import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { Maximize2 } from "lucide-react";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  type CarouselApi,
} from "@/components/ui/carousel";
import { PlatformMap } from "@/components/PlatformMap";
import { HeartRateZoneChart } from "@/components/HeartRateZoneChart";
import { RouteHrEffortChart } from "@/components/RouteHrEffortChart";
import { buildZoneColoredRouteSegments } from "@/lib/routeHrZones";
import { formatPaceSecPerKm, formatElapsedTime } from "@/lib/formatPace";
import { useUnitFormatters } from "@/hooks/useUnits";
import { formatMetersLabel, paceUnitLabel, formatPaceDisplay, splitUnitShort } from "@/lib/units";
import type { HrZone, HrZoneBuckets, HrZoneId } from "@/lib/heartRateZones";
import { cn } from "@/lib/utils";

/** Shared map/HR carousel surface — must match after-run summary (`RunningActive`). */
export const SUMMARY_MAP_CAROUSEL_SURFACE_CLASS =
  "rounded-lg overflow-hidden border border-border/30 h-44 min-h-[176px] w-full cursor-pointer";

function parseMmSs(text: string): number | null {
  const cleaned = text.replace(/\/(?:km|mi)$/i, '').trim();
  const parts = cleaned.split(':').map((p) => parseInt(p, 10));
  if (parts.some((n) => !Number.isFinite(n))) return null;
  if (parts.length === 3) return parts[0] * 3600 + parts[1] * 60 + parts[2];
  if (parts.length === 2) return parts[0] * 60 + parts[1];
  return null;
}

/** Gray column = cumulative elapsed; green = per-split pace (handles legacy segment-time rows). */
function kmSplitsForDisplay(splits: { km: number; time: string; pace: string; isPartial?: boolean }[]) {
  let cumulative = 0;
  return splits.map((split) => {
    const paceSec = parseMmSs(split.pace);
    const timeSec = parseMmSs(split.time);
    let cumulativeSec: number;
    if (split.isPartial) {
      cumulativeSec = timeSec ?? cumulative;
    } else if (timeSec != null && paceSec != null && timeSec >= cumulative + paceSec - 2) {
      // Legacy: time was cumulative and pace was wall-clock for the split unit.
      // When pace is stored as sec/km, cumulative time is authoritative.
      cumulativeSec = timeSec;
      cumulative = timeSec;
    } else {
      cumulative += paceSec ?? timeSec ?? 0;
      cumulativeSec = cumulative;
    }
    return {
      ...split,
      paceSec,
      cumulativeDisplay: formatElapsedTime(cumulativeSec),
    };
  });
}

export type SummaryHeartRate = {
  zones: HrZone[];
  buckets: HrZoneBuckets;
  maxHr: number;
  maxHrSource?: "measured" | "estimated";
  avgBpm?: number | null;
  peakBpm?: number | null;
  targetZone?: HrZoneId;
  samples?: { timestamp: number; bpm: number }[];
};

function CarouselExpandSurface({
  onExpand,
  ariaLabel,
  carouselApi,
  className,
  children,
}: {
  onExpand: () => void;
  ariaLabel: string;
  carouselApi?: CarouselApi;
  className?: string;
  children: ReactNode;
}) {
  const touchRef = useRef({ x: 0, y: 0, t: 0, moved: false });
  const dragRef = useRef(false);

  useEffect(() => {
    if (!carouselApi) return;
    const onScroll = () => { dragRef.current = true; };
    const onSettle = () => { dragRef.current = false; };
    carouselApi.on("scroll", onScroll);
    carouselApi.on("settle", onSettle);
    return () => {
      carouselApi.off("scroll", onScroll);
      carouselApi.off("settle", onSettle);
    };
  }, [carouselApi]);

  return (
    <div
      className={cn("relative", className)}
      onTouchStart={(e) => {
        dragRef.current = false;
        touchRef.current = {
          x: e.touches[0].clientX,
          y: e.touches[0].clientY,
          t: Date.now(),
          moved: false,
        };
      }}
      onTouchMove={(e) => {
        const dx = e.touches[0].clientX - touchRef.current.x;
        const dy = e.touches[0].clientY - touchRef.current.y;
        if (Math.hypot(dx, dy) > 14) touchRef.current.moved = true;
      }}
      onTouchEnd={() => {
        if (dragRef.current || touchRef.current.moved) return;
        if (Date.now() - touchRef.current.t > 400) return;
        onExpand();
      }}
      onClick={() => {
        if (dragRef.current || touchRef.current.moved) return;
        onExpand();
      }}
      role="button"
      tabIndex={0}
      aria-label={ariaLabel}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onExpand();
        }
      }}
    >
      {children}
    </div>
  );
}

export function SummaryMapHrCarousel({
  mapCenter,
  routeSegments,
  routePoints,
  routeLine,
  coloredRouteSegments,
  onExpandMap,
  onExpandHr,
  onExpandCombined,
  expandMapAria,
  expandHrAria,
  expandCombinedAria,
  heartRate,
}: {
  mapCenter: [number, number] | null;
  routeSegments: [number, number][][];
  routePoints: { lat: number; lng: number; timestamp?: number }[];
  routeLine: [number, number][];
  coloredRouteSegments: ReturnType<typeof buildZoneColoredRouteSegments>;
  onExpandMap?: () => void;
  onExpandHr?: () => void;
  onExpandCombined?: () => void;
  expandMapAria: string;
  expandHrAria: string;
  expandCombinedAria: string;
  heartRate: SummaryHeartRate;
}) {
  const [carouselApi, setCarouselApi] = useState<CarouselApi>();
  const [page, setPage] = useState(0);
  const pageCount = 3;

  useEffect(() => {
    if (!carouselApi) return;
    const onSelect = () => setPage(carouselApi.selectedScrollSnap());
    carouselApi.on("select", onSelect);
    onSelect();
    return () => {
      carouselApi.off("select", onSelect);
    };
  }, [carouselApi]);

  const pageHints = ["Swipe for heart rate zones →", "Swipe for route + HR effort →", ""];

  return (
    <div className="shrink-0 mb-2">
      <Carousel
        setApi={setCarouselApi}
        opts={{ align: "start", loop: false, dragFree: false, duration: 28, skipSnaps: false }}
        className="w-full touch-pan-y"
      >
        <CarouselContent className="ml-0">
          <CarouselItem className="pl-0 basis-full">
            <CarouselExpandSurface
              carouselApi={carouselApi}
              onExpand={onExpandMap ?? (() => {})}
              ariaLabel={expandMapAria}
              className={SUMMARY_MAP_CAROUSEL_SURFACE_CLASS}
            >
              <PlatformMap
                key="summary-map-carousel"
                center={mapCenter}
                routePoints={routeLine}
                routeSegments={routeSegments}
                showUserMarker={false}
                showRoute
                showStartFinishMarkers
                showKmMarkers
                fitToRoute
                fitPaddingBottom={1.25}
                autoFollow={false}
                className="w-full h-full pointer-events-none"
                darkMode
                interactive={false}
                zoom={12}
              />
              {onExpandMap ? (
                <div className="absolute bottom-2 right-2 z-20 flex items-center justify-center w-10 h-10 rounded-lg bg-black/70 pointer-events-none" aria-hidden>
                  <Maximize2 className="h-5 w-5 text-white" />
                </div>
              ) : null}
            </CarouselExpandSurface>
          </CarouselItem>
          <CarouselItem className="pl-0 basis-full">
            <CarouselExpandSurface
              carouselApi={carouselApi}
              onExpand={onExpandHr ?? (() => {})}
              ariaLabel={expandHrAria}
              className="rounded-lg border border-border/30 bg-muted/10 p-2 min-h-[148px] max-h-40 overflow-y-auto cursor-pointer"
            >
              <HeartRateZoneChart
                zones={heartRate.zones}
                buckets={heartRate.buckets}
                maxHr={heartRate.maxHr}
                maxHrSource={heartRate.maxHrSource}
                avgBpm={heartRate.avgBpm}
                peakBpm={heartRate.peakBpm}
                targetZone={heartRate.targetZone}
                className="text-xs"
              />
            </CarouselExpandSurface>
          </CarouselItem>
          <CarouselItem className="pl-0 basis-full">
            <CarouselExpandSurface
              carouselApi={carouselApi}
              onExpand={onExpandCombined ?? (() => {})}
              ariaLabel={expandCombinedAria}
              className="rounded-lg border border-border/30 bg-muted/10 p-2 min-h-[148px] max-h-40 overflow-hidden cursor-pointer"
            >
              <div className="h-24 rounded overflow-hidden mb-2 border border-border/20">
                <PlatformMap
                  center={mapCenter}
                  routePoints={routeLine}
                  routeSegments={coloredRouteSegments.length > 0 ? undefined : routeSegments}
                  coloredRouteSegments={coloredRouteSegments.length > 0 ? coloredRouteSegments : undefined}
                  showUserMarker={false}
                  showRoute
                  showStartFinishMarkers
                  showKmMarkers
                  fitToRoute
                  fitPaddingBottom={1.25}
                  autoFollow={false}
                  className="w-full h-full pointer-events-none"
                  darkMode
                  interactive={false}
                  zoom={12}
                />
              </div>
              <RouteHrEffortChart
                routePoints={routePoints}
                hrSamples={heartRate.samples ?? []}
                zones={heartRate.zones}
              />
            </CarouselExpandSurface>
          </CarouselItem>
        </CarouselContent>
      </Carousel>
      <div className="flex justify-center gap-1.5 mt-1.5" aria-hidden>
        {Array.from({ length: pageCount }).map((_, i) => (
          <span key={i} className={cn("h-1 w-5 rounded-full transition-colors", page === i ? "bg-primary" : "bg-muted-foreground/30")} />
        ))}
      </div>
      {pageHints[page] ? (
        <p className="text-[10px] text-center text-muted-foreground mt-0.5">{pageHints[page]}</p>
      ) : null}
    </div>
  );
}

export function formatSegmentDistance(meters: number, units: "metric" | "imperial" = "metric") {
  return formatMetersLabel(meters, units);
}

export function SummarySplitsSegmentsCarousel({
  kmSplits,
  runSegments,
}: {
  kmSplits: { km: number; time: string; pace: string }[];
  runSegments: {
    index: number;
    durationSeconds: number;
    distanceMeters: number;
    time: string;
    avgHeartRateBpm?: number;
  }[];
}) {
  const { units, formatPace, paceSuffix } = useUnitFormatters();
  const [carouselApi, setCarouselApi] = useState<CarouselApi>();
  const [page, setPage] = useState(0);
  const pageCount = 2;
  const pageHints = ["Swipe for segments →", ""];
  const displayedSplits = useMemo(() => kmSplitsForDisplay(kmSplits), [kmSplits]);
  const splitLabel = splitUnitShort(units);
  const splitsTitle = units === "imperial" ? "Mile Splits" : "Km Splits";

  useEffect(() => {
    if (!carouselApi) return;
    const onSelect = () => setPage(carouselApi.selectedScrollSnap());
    carouselApi.on("select", onSelect);
    onSelect();
    return () => {
      carouselApi.off("select", onSelect);
    };
  }, [carouselApi]);

  return (
    <div className="shrink-0">
      <Carousel
        setApi={setCarouselApi}
        opts={{ align: "start", loop: false, dragFree: false, duration: 28, skipSnaps: false }}
        className="w-full touch-pan-y"
      >
        <CarouselContent className="ml-0">
          <CarouselItem className="pl-0 basis-full">
            <div className="rounded-lg border border-border/30 bg-muted/10 p-2 min-h-[120px]">
              <label className="text-xs text-muted-foreground mb-1 block">{splitsTitle}</label>
              {displayedSplits.length > 0 ? (
                <div className="space-y-0.5 max-h-28 overflow-y-auto">
                  {displayedSplits.map((split, idx) => {
                    const paceShown =
                      split.paceSec != null && split.paceSec > 0
                        ? formatPaceDisplay(split.paceSec, units)
                        : split.pace;
                    return (
                    <div key={idx} className="flex justify-between items-center text-xs px-2 py-1 rounded-md hover:bg-muted/20">
                      <span className="text-muted-foreground font-medium">{split.km} {splitLabel}</span>
                      <div className="flex items-center gap-3">
                        <span className="text-xs text-muted-foreground">{split.cumulativeDisplay}</span>
                        <span className="font-mono font-semibold text-primary">{paceShown}{paceSuffix}</span>
                      </div>
                    </div>
                    );
                  })}
                </div>
              ) : (
                <p className="text-[11px] text-muted-foreground px-1">No {splitLabel} splits recorded</p>
              )}
            </div>
          </CarouselItem>
          <CarouselItem className="pl-0 basis-full">
            <div className="rounded-lg border border-border/30 bg-muted/10 p-2 min-h-[120px]">
              <label className="text-xs text-muted-foreground mb-1 block">Segments</label>
              {runSegments.length > 0 ? (
                <div className="space-y-1 max-h-28 overflow-y-auto">
                  {[...runSegments].reverse().map((seg) => {
                    const paceSec = seg.durationSeconds > 0 && seg.distanceMeters > 0
                      ? seg.durationSeconds / (seg.distanceMeters / 1000)
                      : 0;
                    return (
                      <div key={seg.index} className="px-2 py-1.5 rounded-md hover:bg-muted/20 space-y-1">
                        <span className="text-muted-foreground font-medium text-xs">Segment {seg.index}</span>
                        <div className="grid grid-cols-3 gap-2 text-[10px] text-muted-foreground">
                          <span>Time</span>
                          <span>Distance</span>
                          <span className="text-right">Pace{paceSuffix}</span>
                        </div>
                        <div className="grid grid-cols-3 gap-2 text-xs">
                          <span className="font-mono text-muted-foreground">{seg.time}</span>
                          <span className="font-mono text-muted-foreground">{formatSegmentDistance(seg.distanceMeters, units)}</span>
                          <span className="font-mono font-semibold text-primary text-right">
                            {paceSec > 0 ? `${formatPace(paceSec)}${paceSuffix}` : `--:--${paceSuffix}`}
                          </span>
                        </div>
                        {seg.avgHeartRateBpm != null && seg.avgHeartRateBpm > 0 ? (
                          <div className="text-[10px] text-muted-foreground">Avg HR {Math.round(seg.avgHeartRateBpm)} bpm</div>
                        ) : null}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="text-[11px] text-muted-foreground px-1">No segments marked</p>
              )}
            </div>
          </CarouselItem>
        </CarouselContent>
      </Carousel>
      <div className="flex justify-center gap-1.5 mt-1.5" aria-hidden>
        {Array.from({ length: pageCount }).map((_, i) => (
          <span key={i} className={cn("h-1 w-5 rounded-full transition-colors", page === i ? "bg-primary" : "bg-muted-foreground/30")} />
        ))}
      </div>
      {pageHints[page] ? (
        <p className="text-[10px] text-center text-muted-foreground mt-0.5">{pageHints[page]}</p>
      ) : null}
    </div>
  );
}
